/**
 * A group's published layouts, copied into a member's workspace — once each,
 * hidden (`docs/plans/group-accounts.md` §2).
 *
 * **Client-side on load, not server-side on join**, because one rule then
 * covers joining, being added by a Group Admin, and the group publishing a new
 * layout next month. The server never writes into a workspace it is not saving.
 *
 * **Hidden, so a new desk never pushes into the tab strip uninvited**: it waits
 * in the Layout panel until the member shows it.
 *
 * **`adoptedPresets` is what stops a copy coming back.** Hidden means "not now";
 * deleting means "never", and without the record the next load would re-add
 * what the member just removed.
 *
 * Pure: the fetch and the dispatch live in `App`, where they can fail without
 * this module having to know.
 */
import type { BriefBoardConfig, Layout, ScreenerConfig, Section, UserWorkspace } from "@qufolio/model";
import type { LayoutSummary, ScreenOverride } from "../api/layouts";
import { screenerConfig } from "./section-config";
import { briefBoardConfig, carryMine, hasMine } from "./brief-board";
import { isMacroKind } from "./macro-sections";

/**
 * A virtual group's includes, by its key — `/auth/me`'s `virtualGroups`
 * (`docs/plans/group-sharing.md`): `{"grp-jas-jp": ["grp-jas", "grp-jp"]}`.
 */
export type VirtualGroups = Readonly<Record<string, readonly string[]>>;

/**
 * The groups whose layouts never reach the account being authored: the group
 * itself, and — for a virtual group — every group it includes.
 *
 * `memberOf` for a virtual group's own account names JAS and JP as well (the
 * server reports the includes there so members receive them), and copying
 * those into the account that sets the overrides would give it a second,
 * stale JAS Layout beside the inherited row it is meant to read.
 */
function authoredGroups(authoringFor: string | null, virtualGroups?: VirtualGroups): Set<string> {
  if (authoringFor === null) return new Set();
  return new Set([authoringFor, ...(virtualGroups?.[authoringFor] ?? [])]);
}

/** A group's publication that this member receives — the gate every rule here shares. */
function received(
  preset: LayoutSummary,
  groups: ReadonlySet<string>,
  authored: ReadonlySet<string>,
): boolean {
  return (
    preset.ownerKind === "org" &&
    preset.ownerKey !== null &&
    groups.has(preset.ownerKey) &&
    !authored.has(preset.ownerKey)
  );
}

/**
 * The published layouts this member should receive and has not yet.
 *
 * A group's (`ownerKind: "org"`) and only one the member **belongs** to — an
 * admin's listing names every group's, and joining none of them should copy
 * none of them. Never the group being authored as: its account holds the
 * drafts those presets were published from, and copying them back into it
 * would put every desk in twice.
 *
 * **A virtual group's member arrives here with no rule of its own**: the
 * server puts the included groups in `memberOf`, so JAS's and JP's layouts
 * pass the gate exactly as they do for a JAS or a JP member, each key once
 * however many of the member's groups it is reachable through.
 */
export function layoutsToAdopt(
  presets: readonly LayoutSummary[],
  adopted: readonly string[] | undefined,
  memberOf: readonly string[],
  authoringFor: string | null,
  virtualGroups?: VirtualGroups,
): LayoutSummary[] {
  const had = new Set(adopted ?? []);
  const groups = new Set(memberOf);
  const authored = authoredGroups(authoringFor, virtualGroups);
  return presets.filter((preset) => received(preset, groups, authored) && !had.has(preset.key));
}

/**
 * The name each received layout arrives under, by preset key.
 *
 * **Two inherited layouts may share a name** once a member receives from more
 * than one group — a virtual group's member gets JAS's and JP's — and two tabs
 * called *Market Breadth* cannot be told apart (`docs/plans/group-sharing.md`,
 * rule 2). The first, by group key, keeps its name; every later one arrives as
 * `Market Breadth (JP)`. **Ordered by group key, not by listing order**, so the
 * same member gets the same names on every load and on every machine — a name
 * that swapped between loads would make the replace look like a rename.
 *
 * Only layouts this member receives compete: an admin's listing names every
 * group's, and a group the member is not in must not push a suffix onto one
 * they are.
 */
export function adoptedNames(
  presets: readonly LayoutSummary[],
  memberOf: readonly string[],
  authoringFor: string | null,
  virtualGroups?: VirtualGroups,
  known?: ReadonlyMap<string, string>,
): Map<string, string> {
  const groups = new Set(memberOf);
  const authored = authoredGroups(authoringFor, virtualGroups);
  const ordered = presets
    .filter((preset) => received(preset, groups, authored))
    .sort((a, b) => a.ownerKey!.localeCompare(b.ownerKey!) || a.key.localeCompare(b.key));
  const taken = new Set<string>();
  const names = new Map<string, string>();
  for (const preset of ordered) {
    const name = taken.has(preset.name)
      ? `${preset.name} (${groupDisplayName(preset.ownerKey!, preset.ownerName, known)})`
      : preset.name;
    taken.add(preset.name);
    names.set(preset.key, name);
  }
  return names;
}

// ── screener overrides (docs/plans/group-sharing.md) ─────────────────────────

/** What a screen's definition fills a screener section with. */
export interface ScreenDefinition {
  columns?: ScreenerConfig["columns"];
  filter?: ScreenerConfig["filter"];
  sort?: ScreenerConfig["sort"];
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  /** A JP trade-list screen; absent or null is ordinary. */
  recs?: ScreenerConfig["recs"] | null;
  /** A TopDown screen; absent or null is ordinary. */
  topDown?: ScreenerConfig["topDown"] | null;
  /** A Seasonality screen; absent or null is ordinary. */
  seasonality?: ScreenerConfig["seasonality"] | null;
}

/**
 * The screens an override would switch this layout's screeners **to** — what
 * the caller must fetch before `withScreenOverrides` can apply them. Empty when
 * no screener shows a `from`, which is the common case and costs no request.
 */
export function overrideTargets(
  layout: Pick<Layout, "sections">,
  overrides: readonly ScreenOverride[],
): string[] {
  const showing = new Set(
    layout.sections.flatMap((section) => {
      const ref = screenerConfig(section)?.screenRef;
      return ref ? [ref] : [];
    }),
  );
  return [...new Set(overrides.filter((o) => showing.has(o.from)).map((o) => o.to))];
}

/**
 * A member's copy with the virtual group's overrides applied: every screener
 * showing `from` shows `to` instead — **with `to`'s columns, conditions, sort
 * and library**, so nothing of `from` lingers in the section's inline copy.
 *
 * Applied to the member's copy as it is made or replaced, never to the
 * published layout, so a JAS-only member's *JAS Layout* still opens on
 * Impulse. Each section is matched on the screen it showed **as published**,
 * so two overrides never chain (`A → B`, `B → C` leaves an A screener on B).
 *
 * **A `to` whose definition could not be read is not applied.** Switching the
 * ref alone would put Impulse's columns under a Combo tab, and a member's
 * screener refills a group's screen only when its tab is clicked — so the
 * wrong copy would stand until then. Left on `from`, the next replace tries
 * again.
 *
 * **The library is replaced outright here**, unlike `applyScreenTo`, where a
 * screen with no `savedFilters` keeps the panel's: the panel's library is the
 * one the override exists to remove. Ticks are pruned to the library that
 * arrives, `applyScreenTo`'s second rule and for its reason.
 */
export function withScreenOverrides(
  layout: Layout,
  overrides: readonly ScreenOverride[],
  definitions: ReadonlyMap<string, ScreenDefinition>,
): Layout {
  const to = new Map(overrides.map((o) => [o.from, o.to]));
  let changed = false;
  const sections = layout.sections.map((section) => {
    const config = screenerConfig(section);
    const target = config?.screenRef ? to.get(config.screenRef) : undefined;
    const screen = target ? definitions.get(target) : undefined;
    if (!config || !target || !screen) return section;
    changed = true;
    const savedFilters = screen.savedFilters ?? [];
    const library = new Set(savedFilters.map((f) => f.id));
    const {
      filter: _filter,
      activeFilterRef,
      activeFilterRefs,
      // The screen switched to decides whether this is a trade list.
      recs: _recs,
      // And whether it is a TopDown, or a Seasonality screen.
      topDown: _topDown,
      seasonality: _seasonality,
      ...rest
    } = config;
    const next: ScreenerConfig = {
      ...rest,
      screenRef: target,
      columns: screen.columns ?? [],
      ...(screen.filter ? { filter: screen.filter } : {}),
      sort: screen.sort ?? [],
      savedFilters,
      savedFilterCategoryOrder: screen.savedFilterCategoryOrder ?? [],
      ...(screen.recs ? { recs: screen.recs } : {}),
      ...(screen.topDown ? { topDown: screen.topDown } : {}),
      ...(screen.seasonality ? { seasonality: screen.seasonality } : {}),
      ...(activeFilterRef && library.has(activeFilterRef) ? { activeFilterRef } : {}),
      activeFilterRefs: (activeFilterRefs ?? []).filter((id) => library.has(id)),
    };
    return { ...section, config: next };
  });
  return changed ? { ...layout, sections } : layout;
}

/** One fetched preset, already made this workspace's by `adoptLayout`. */
export interface Adopted {
  key: string;
  layout: Layout;
  /** The preset's version when fetched, recorded on the copy's `source`. */
  version?: number;
}

/**
 * The published layouts whose member copy is behind — republished since it was
 * copied — and so replaced on this load (`docs/plans/group-accounts.md` §5:
 * publish replaces for every member; versions are the group admin's).
 *
 * A copy made before its preset had any version reads as version 0, so the
 * first versioned publish replaces it too. A preset without a version has
 * nothing to compare and replaces nothing.
 */
export function layoutsToReplace(
  presets: readonly LayoutSummary[],
  ws: UserWorkspace,
): LayoutSummary[] {
  const copies = new Map<string, number>();
  for (const layout of ws.layouts) {
    if (layout.source) copies.set(layout.source.preset, layout.source.version ?? 0);
  }
  return presets.filter((preset) => {
    const had = copies.get(preset.key);
    return had !== undefined && preset.version !== undefined && had < preset.version;
  });
}

/**
 * The workspace with each behind copy swapped for the fresh one, **in place**:
 * same position, and still hidden or shown as the member left it. The member's
 * own edits to it are gone — that is what publish means here, and Duplicate is
 * how a member keeps their own (§5).
 *
 * **Except a Daily Brief's own board.** `mine` is the member's half of a
 * `briefBoard` section by design (`docs/plans/daily-brief.md`), and it sits
 * beside the preset rather than in place of it — so it is carried onto the
 * fresh section (`boardsCarried` says how they are paired), and the board offers "a newer preset" from
 * `mineBasedOn` instead of wiping it on every publish.
 */
export function withReplaced(ws: UserWorkspace, copies: readonly Adopted[]): UserWorkspace {
  const fresh = new Map(copies.map((copy) => [copy.key, copy]));
  let changed = false;
  const layouts = ws.layouts.map((layout) => {
    const copy = layout.source ? fresh.get(layout.source.preset) : undefined;
    if (!copy || (layout.source?.version ?? 0) >= (copy.version ?? 0)) return layout;
    changed = true;
    const { hidden: _drop, ...rest } = copy.layout;
    return {
      ...rest,
      sections: macroCarried(boardsCarried(rest.sections, layout.sections), layout.sections),
      ...(layout.hidden ? { hidden: true } : {}),
      source: { preset: copy.key, ...(copy.version ? { version: copy.version } : {}) },
    };
  });
  return changed ? { ...ws, layouts } : ws;
}

/**
 * The fresh sections, each Daily Brief keeping the member's board from the old
 * copy's matching one.
 *
 * **Not matched by section id**: every copy comes through `adoptLayout`, which
 * mints the section ids afresh, so the old copy's and the fresh copy's never
 * agree. The widget ids inside the preset do survive adopting, and an author's
 * edit keeps the ones it did not remove — so a fresh board is paired with the
 * old one whose preset shares a widget with it, and failing that, the k-th
 * unpaired board with the k-th.
 */
function boardsCarried(fresh: Section[], old: readonly Section[]): Section[] {
  const had = old.flatMap((section) => {
    const config = briefBoardConfig(section);
    return config && hasMine(config) ? [config] : [];
  });
  if (!had.length) return fresh;
  const boards = fresh.flatMap((section, at) => {
    const config = briefBoardConfig(section);
    return config ? [{ at, config }] : [];
  });
  const pair = new Map<number, BriefBoardConfig>();
  const taken = new Set<BriefBoardConfig>();
  for (const { at, config } of boards) {
    const ids = new Set(config.preset.widgets.map((w) => w.id));
    const match = had.find((o) => !taken.has(o) && o.preset.widgets.some((w) => ids.has(w.id)));
    if (match) {
      pair.set(at, match);
      taken.add(match);
    }
  }
  const rest = had.filter((o) => !taken.has(o));
  for (const { at } of boards) if (!pair.has(at) && rest.length) pair.set(at, rest.shift()!);
  return fresh.map((section, at) => {
    const from = pair.get(at);
    const config = from && briefBoardConfig(section);
    return config ? { ...section, config: carryMine(config, from) } : section;
  });
}

/**
 * The fresh sections, each macro section keeping the member's settings from
 * the old copy's section of the same kind (`lib/macro-sections.ts`).
 *
 * A macro config is how one reader looks at the section — a calendar's range,
 * the curve's window, the matrix's mode — never content the author publishes,
 * so a republish must not reset it, for the reason a Daily Brief keeps `mine`.
 * Matched by kind and then order, the k-th `yieldCurve` with the k-th: section
 * ids are minted afresh by `adoptLayout`, and these configs hold no ids of their
 * own to pair by. A kind the old copy did not have takes the author's settings.
 */
function macroCarried(fresh: Section[], old: readonly Section[]): Section[] {
  const had = new Map<string, Section[]>();
  for (const section of old)
    if (isMacroKind(section.kind) && section.config)
      had.set(section.kind, [...(had.get(section.kind) ?? []), section]);
  if (!had.size) return fresh;
  return fresh.map((section) => {
    const from = isMacroKind(section.kind) ? had.get(section.kind)?.shift() : undefined;
    return from ? { ...section, config: structuredClone(from.config) } : section;
  });
}

/**
 * A published layout that arrives **shown**: one holding the Daily Brief
 * (owner, 2026-10-18 — "make this the default" for JAS members). Every other
 * group layout keeps arriving hidden, shown from ✎ layouts.
 */
export function arrivesShown(layout: Pick<Layout, "sections">): boolean {
  return (layout.sections ?? []).some((section) => section.kind === "briefBoard");
}

/**
 * The workspace with the copies appended, hidden (unless `arrivesShown`) and
 * marked with their source, and their keys recorded. One change, so one undo
 * and one save.
 *
 * A key already recorded is skipped here as well as in `layoutsToAdopt`: two
 * loads racing each other must not append the same desk twice.
 */
export function withAdopted(ws: UserWorkspace, copies: readonly Adopted[]): UserWorkspace {
  const had = new Set(ws.adoptedPresets ?? []);
  const fresh = copies.filter((copy) => !had.has(copy.key) && (had.add(copy.key), true));
  if (fresh.length === 0) return ws;
  return {
    ...ws,
    adoptedPresets: [...(ws.adoptedPresets ?? []), ...fresh.map((copy) => copy.key)],
    layouts: [...ws.layouts, ...fresh.map((copy) => linkedCopy(copy, !arrivesShown(copy.layout)))],
  };
}

/** A fetched copy as a workspace layout: marked with its source, hidden or shown. */
function linkedCopy(copy: Adopted, hidden: boolean): Layout {
  const { hidden: _drop, ...rest } = copy.layout;
  return {
    ...rest,
    ...(hidden ? { hidden: true } : {}),
    source: { preset: copy.key, ...(copy.version ? { version: copy.version } : {}) },
  };
}

/** `adoptedPresets` with `key` in it, once. */
function recorded(ws: UserWorkspace, key: string): string[] {
  const had = ws.adoptedPresets ?? [];
  return had.includes(key) ? [...had] : [...had, key];
}

// ── in-session refresh (docs/plans/group-layout-sync.md, step 2) ─────────────

/**
 * The behind copies that may be replaced **now**: all of them except the one
 * on screen.
 *
 * Owner's decision 3 (2026-10-06): an in-session replace waits until the
 * member is not on that layout. Swapping the desk somebody is working at, a
 * second after they brought the tab back to the front, reads as the app
 * throwing their charts away; the listing's version number moves at once, and
 * the copy follows the moment they switch. The caller decides when this
 * applies — on the first listing of a page load nothing is on screen yet that
 * the member has started working with, and everything is replaced as before.
 */
export function deferActive(
  stale: readonly LayoutSummary[],
  ws: UserWorkspace,
  activeLayoutId: string | undefined,
): LayoutSummary[] {
  if (activeLayoutId === undefined) return [...stale];
  const active = ws.layouts.find((layout) => layout.id === activeLayoutId);
  const onScreen = active?.source?.preset;
  return onScreen === undefined ? [...stale] : stale.filter((preset) => preset.key !== onScreen);
}

// ── "From your club" (step 3) ────────────────────────────────────────────────

/** Where the member's linked copy of `key` sits, or `-1` when they hold none. */
export function linkedCopyIndex(ws: UserWorkspace, key: string): number {
  return ws.layouts.findIndex((layout) => layout.source?.preset === key);
}

/**
 * A group layout applied by hand: appended **shown** and linked exactly as an
 * adopted copy is, so the group's next publish replaces it too.
 *
 * Before this the panel added a plain layout — no `source`, nothing in
 * `adoptedPresets` — and that copy was invisible to `layoutsToReplace`, which
 * is how a member came to hold a stale *JAS Layout* beside the fresh one
 * (`docs/plans/group-layout-sync.md`). Shown, unlike an adopted copy, because
 * the member just asked for it.
 *
 * A linked copy already held is not added again: the caller selects that one.
 */
export function withLinked(ws: UserWorkspace, copy: Adopted): UserWorkspace {
  if (linkedCopyIndex(ws, copy.key) >= 0) return ws;
  return {
    ...ws,
    adoptedPresets: recorded(ws, copy.key),
    layouts: [...ws.layouts, linkedCopy(copy, false)],
  };
}

// ── members choose (owner, 2026-10-18) ───────────────────────────────────────
//
// `docs/features/group-accounts.md` › *Members choose; Promote & Publish*. A
// publish no longer changes anyone's layouts: a new group layout and a new
// version both wait in Shared with me, marked ● new, until the member takes
// them. Only a layout holding the Daily Brief is still copied in by itself.

/**
 * Whether a group layout is copied into a member's panel by itself, without
 * the member asking: only one holding the Daily Brief (`briefBoard`, sent by
 * the listing). Everything else is the member's to take from Shared with me.
 */
export function adoptsByItself(preset: Pick<LayoutSummary, "briefBoard">): boolean {
  return preset.briefBoard === true;
}

/**
 * What a group layout is to this member, for its Shared with me row:
 *
 * - `behind` — they hold a linked copy older than the published version;
 * - `new` — no linked copy, and the key was never taken (not in
 *   `adoptedPresets`), so it was published since they last looked;
 * - `held` — a linked copy at the published version;
 * - `none` — not one of their groups', or taken once and since deleted.
 *
 * Members who held every layout before this change have every key recorded,
 * so only what is published after it reads `new`.
 */
export type SharedState = "behind" | "new" | "held" | "none";

export function sharedState(
  preset: LayoutSummary,
  ws: UserWorkspace,
  memberOf: readonly string[],
  authoringFor: string | null,
  virtualGroups?: VirtualGroups,
): SharedState {
  if (!received(preset, new Set(memberOf), authoredGroups(authoringFor, virtualGroups))) return "none";
  const at = linkedCopyIndex(ws, preset.key);
  if (at >= 0) {
    const had = ws.layouts[at]!.source?.version ?? 0;
    return preset.version !== undefined && had < preset.version ? "behind" : "held";
  }
  return (ws.adoptedPresets ?? []).includes(preset.key) ? "none" : "new";
}

/**
 * **Add as "JAS Layout v3"**: the fresh copy appended shown and linked, and the
 * member's old copy **unlinked** and kept exactly as it is. Unlinked, because
 * one key has one linked copy (`linkedCopyIndex`, and the store keeps the first
 * `source` per key) — left linked, the old copy would read behind for ever.
 */
export function withAddedVersion(ws: UserWorkspace, copy: Adopted): UserWorkspace {
  const layouts = ws.layouts.map((layout) => {
    if (layout.source?.preset !== copy.key) return layout;
    const { source: _unlinked, ...rest } = layout;
    return rest;
  });
  return {
    ...ws,
    adoptedPresets: recorded(ws, copy.key),
    layouts: [...layouts, linkedCopy(copy, false)],
  };
}

/**
 * While authoring as a group: its published layouts the account does not
 * hold, to add, and its linked copies a publish elsewhere left behind, to
 * replace.
 *
 * **Promote & Publish publishes from somebody's own account**, so the group's
 * account never saw the layout — and writing it in from the server raced any
 * tab open in ☰ › Author as, whose next whole-workspace save deleted it
 * (2026-10-18, twice). So the account picks it up here, at load, from the
 * published copy. A layout the account already has **by name** is the one it
 * was published from (`publishedKeyFor`'s second rule) and is left alone; a
 * key in `adoptedPresets` was added once and since deleted, and stays deleted.
 *
 * Only linked copies are replaced, never an author's own draft: the published
 * version is newer than a copy taken from it, and the author's edits to the
 * copy are what Promote would publish over anyway.
 */
export function authorCopiesDue(
  presets: readonly LayoutSummary[],
  ws: UserWorkspace,
  authoringFor: string | null,
): { add: LayoutSummary[]; replace: LayoutSummary[] } {
  if (authoringFor === null) return { add: [], replace: [] };
  const own = presets.filter((p) => p.ownerKind === "org" && p.ownerKey === authoringFor);
  const had = new Set(ws.adoptedPresets ?? []);
  const names = new Set(ws.layouts.map((layout) => layout.name));
  const add = own.filter(
    (p) => linkedCopyIndex(ws, p.key) < 0 && !had.has(p.key) && !names.has(p.name),
  );
  const replace = layoutsToReplace(own, ws);
  return { add, replace };
}

/** One layout in the group's account, and the published version it now is. */
export interface SourceLink {
  layoutId: string;
  key: string;
  version: number;
}

/**
 * While authoring as a group: its own layouts tied to their published one by
 * **name only**, to link at the published version now (owner, 2026-10-18).
 *
 * Unlinked, an author's original could not tell that a Promote or another tab
 * had published past it — Author as JAS sat on Market Breadth v1 after v2
 * went out, one publish from undoing it. Linked at the current version, which
 * assumes the account's copy is what was last published; after this, a publish
 * moves the link (`withSourceSet`) and a newer one replaces it
 * (`authorCopiesDue`). A key the account already links is left alone.
 */
export function originalsToLink(
  presets: readonly LayoutSummary[],
  ws: UserWorkspace,
  authoringFor: string | null,
): SourceLink[] {
  if (authoringFor === null) return [];
  const out: SourceLink[] = [];
  const taken = new Set<string>();
  for (const preset of presets) {
    if (preset.ownerKind !== "org" || preset.ownerKey !== authoringFor || preset.version === undefined) continue;
    if (linkedCopyIndex(ws, preset.key) >= 0) continue;
    const original = ws.layouts.find((l) => !l.source && l.name === preset.name && !taken.has(l.id));
    if (!original) continue;
    taken.add(original.id);
    out.push({ layoutId: original.id, key: preset.key, version: preset.version });
  }
  return out;
}

/**
 * Each named layout linked to its key at its version, and **any other layout
 * linked to that key unlinked** — one key, one layout in the account, or the
 * store keeps whichever comes first. The keys are recorded, as a link is.
 */
export function withSourceSet(ws: UserWorkspace, links: readonly SourceLink[]): UserWorkspace {
  if (links.length === 0) return ws;
  const byLayout = new Map(links.map((link) => [link.layoutId, link]));
  const keys = new Set(links.map((link) => link.key));
  const layouts = ws.layouts.map((layout) => {
    const link = byLayout.get(layout.id);
    if (link) return { ...layout, source: { preset: link.key, version: link.version } };
    if (layout.source && keys.has(layout.source.preset)) {
      const { source: _unlinked, ...rest } = layout;
      return rest;
    }
    return layout;
  });
  const had = ws.adoptedPresets ?? [];
  const adoptedPresets = [...had, ...[...keys].filter((key) => !had.includes(key))];
  return { ...ws, adoptedPresets, layouts };
}

/**
 * The day a version was published, as Shared with me prints it — `Oct 18`, in
 * Pacific time, as every time the owner reads is. `null` when there is none.
 */
export function publishedDay(iso: string | undefined, at?: Date): string | null {
  // The server's `updatedAt` is naive UTC (`2026-10-04T15:45:48.903181`); read
  // bare, a browser would take it as local time and print the wrong day.
  const utc = iso && !/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? `${iso}Z` : iso;
  const when = at ?? (utc ? new Date(utc) : null);
  if (!when || Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/Los_Angeles",
  });
}

/**
 * The key a layout is promoted under: the group's, then the promoter's layout
 * id — so promoting the same layout again publishes its next version even
 * after a rename, with nothing stored to remember it (ULIDs are 26 characters;
 * the key column holds 64).
 */
export function promoteKey(group: string, layoutId: string): string {
  return `${group}-promo-${layoutId.toLowerCase()}`;
}

/**
 * The key Promote & Publish writes in `group`. **A copy of that group's own
 * layout goes back to it** — your *Market Breadth*, linked to JAS's, publishes
 * JAS Market Breadth's next version, not a second layout of the name (owner,
 * 2026-10-18: the first cut refused it as a clash). Anything else is keyed by
 * its own id (`promoteKey`).
 */
export function promoteTargetKey(
  presets: readonly LayoutSummary[],
  group: string,
  layout: Pick<Layout, "id" | "source">,
): string {
  const linked = layout.source?.preset;
  const theirs =
    linked !== undefined &&
    presets.some((p) => p.key === linked && p.ownerKind === "org" && p.ownerKey === group);
  return theirs ? linked : promoteKey(group, layout.id);
}

/**
 * What Promote & Publish would do in one group: a first version, the next one
 * of what this layout was promoted as before, or nothing because the group
 * already has a different layout of that name (`uq_layout_owner_name` would
 * refuse it, and two *FANG* rows in Shared with me could not be told apart).
 */
export type PromotePlan =
  | { kind: "new" }
  | { kind: "next"; from: number }
  | { kind: "clash" };

export function promotePlan(
  presets: readonly LayoutSummary[],
  group: string,
  layout: Pick<Layout, "id" | "source">,
  name: string,
): PromotePlan {
  const key = promoteTargetKey(presets, group, layout);
  const own = presets.filter((p) => p.ownerKind === "org" && p.ownerKey === group);
  if (own.some((p) => p.key !== key && p.name === name.trim())) return { kind: "clash" };
  const before = own.find((p) => p.key === key);
  return before ? { kind: "next", from: before.version ?? 1 } : { kind: "new" };
}

// ── legacy copies: ask once (step 4) ─────────────────────────────────────────

/**
 * A group layout the member already holds an **unlinked** copy of — by name —
 * and has never been asked about.
 *
 * `mineId` is the copy the question is about. `redundantIds` are further
 * hidden, unlinked copies of the same name, removed whichever way the member
 * answers.
 */
export interface LegacyCopy {
  preset: LayoutSummary;
  mineId: string;
  redundantIds: string[];
}

/**
 * The group layouts that get the one-time question.
 *
 * Gated as `layoutsToAdopt` is — a group's, one the member belongs to, never
 * the group being authored. Asked only when **nothing** has recorded the key:
 * no layout links it and `adoptedPresets` does not hold it. That record is what
 * makes the question one-time: either answer writes both, and a member who
 * later deletes the linked copy must not be asked again about the one they kept.
 *
 * **Which copy is "mine", and which are redundant.** The case this exists for
 * is member `R39ALaaY…`: one visible *JAS Layout* applied by hand, and one
 * hidden one adopted on an earlier load whose link was lost. The visible one is
 * the member's — they chose it and work in it — so it is the one asked about,
 * and the hidden unlinked twin is a copy of the group's that nobody chose and
 * the answer supersedes, so it goes. The rule is conservative on purpose:
 *
 * - "mine" is the first **visible** unlinked same-name layout; only when there
 *   is none is it the first hidden one.
 * - Only **hidden** unlinked same-name layouts other than "mine" are removed. A
 *   visible layout is never removed — a second visible copy is left exactly as
 *   it is, because a desk on the tab strip is one the member may be using.
 *
 * Exact name match, as the owner settled: a layout renamed by the member is
 * theirs and not asked about. **The name it would arrive under** (`names`,
 * from `adoptedNames`), so when JAS and JP both publish *Market Breadth* a
 * member's own unlinked copy is asked about once — as JAS's — and not a
 * second time as JP's, which arrives as *Market Breadth (JP)*.
 */
export function legacyCopies(
  presets: readonly LayoutSummary[],
  ws: UserWorkspace,
  memberOf: readonly string[],
  authoringFor: string | null,
  virtualGroups?: VirtualGroups,
  names?: ReadonlyMap<string, string>,
): LegacyCopy[] {
  const groups = new Set(memberOf);
  const authored = authoredGroups(authoringFor, virtualGroups);
  const had = new Set(ws.adoptedPresets ?? []);
  const linked = new Set(
    ws.layouts.flatMap((layout) => (layout.source ? [layout.source.preset] : [])),
  );
  const found: LegacyCopy[] = [];
  for (const preset of presets) {
    if (!received(preset, groups, authored) || had.has(preset.key) || linked.has(preset.key)) {
      continue;
    }
    const name = names?.get(preset.key) ?? preset.name;
    const same = ws.layouts.filter((layout) => !layout.source && layout.name === name);
    if (same.length === 0) continue;
    const mine = same.find((layout) => layout.hidden !== true) ?? same[0]!;
    found.push({
      preset,
      mineId: mine.id,
      redundantIds: same
        .filter((layout) => layout.id !== mine.id && layout.hidden === true)
        .map((layout) => layout.id),
    });
  }
  return found;
}

/** The member's answer: the mock's two buttons. */
export type LegacyAnswer = "replace" | "keep";

/**
 * The workspace after the member answered — one change, so one undo and one save.
 *
 * **Replace mine** (the default, owner's decision 1): the member's layout
 * becomes the group's definition **in place** — its id, its position, and
 * hidden or shown as they left it — linked, so later publishes replace it
 * silently. Its id is kept, unlike `withReplaced`'s, so nothing addressing the
 * member's own desk by id loses it at the moment they agreed to the swap.
 *
 * **Keep mine and add theirs** (decision 2): the member's layout is untouched
 * and unlinked; the group's arrives beside it, shown, linked, and named
 * `JAS Layout (JAS)` so the two can be told apart on the tab strip.
 *
 * Either way the key is recorded and the redundant hidden copies go.
 */
export function withLegacyResolved(
  ws: UserWorkspace,
  legacy: { key: string; mineId: string; redundantIds: readonly string[] },
  copy: Adopted,
  answer: LegacyAnswer,
  groupName: string,
): UserWorkspace {
  const drop = new Set(legacy.redundantIds);
  drop.delete(legacy.mineId);
  const kept = ws.layouts.filter((layout) => !drop.has(layout.id));
  const mine = kept.find((layout) => layout.id === legacy.mineId);
  if (!mine) return ws;
  const layouts =
    answer === "replace"
      ? kept.map((layout) =>
          layout.id === legacy.mineId
            ? { ...linkedCopy(copy, layout.hidden === true), id: layout.id, name: layout.name }
            : layout,
        )
      : [
          ...kept,
          linkedCopy({ ...copy, layout: { ...copy.layout, name: `${mine.name} (${groupName})` } }, false),
        ];
  return { ...ws, adoptedPresets: recorded(ws, legacy.key), layouts };
}

/** A group's own org key starts with one of these — the server's `caps.GROUP_KEY_PREFIXES`. */
const GROUP_KEY_PREFIXES = ["grp-", "st-"] as const;

/**
 * A group's name as a member reads it — `JAS`, not `grp-jas`.
 *
 * The layout listing does not carry `ownerName` yet (the screen and chart
 * template listings do), so the caller may pass the names it learned from those;
 * failing all of them, a group key is the name after its prefix in capitals
 * (`grp-jas`, `st-jas` → `JAS`), which is how every group key has been minted.
 */
export function groupDisplayName(
  ownerKey: string,
  ownerName?: string | null,
  known?: ReadonlyMap<string, string>,
): string {
  if (ownerName) return ownerName;
  const learned = known?.get(ownerKey);
  if (learned) return learned;
  const prefix = GROUP_KEY_PREFIXES.find((p) => ownerKey.startsWith(p));
  return prefix ? ownerKey.slice(prefix.length).toUpperCase() : ownerKey;
}

// ── publishing (step 5) ──────────────────────────────────────────────────────

/**
 * The key a layout already holds under `publishTarget`, when it is published.
 *
 * **Its `source` first**, because that survives a rename: a layout applied from
 * the group's own listing and renamed must republish over the original, not
 * beside it. **Then the name**, which is all an author's own draft has. Only a
 * layout matching neither is new, and gets a slug — the rule `publishToClub`
 * follows for screens since `62ad4ee6`.
 */
export function publishedKeyFor(
  layout: Pick<Layout, "name" | "source">,
  name: string,
  presets: readonly LayoutSummary[],
  publishTarget: string,
): string | undefined {
  const own = presets.filter((p) => p.ownerKind === "org" && p.ownerKey === publishTarget);
  const bySource = layout.source && own.find((p) => p.key === layout.source!.preset);
  if (bySource) return bySource.key;
  return own.find((p) => p.name === name)?.key;
}

// ── a group's layout, as members will see it ────────────────────────────────

/**
 * Whether a group's members can open `screenRef`: the group's own screens and
 * the product's. A private screen (the author's own, like a Recs preview) or
 * another group's is not one — published as it stands, members' panels pointed
 * at a screen they cannot open (owner, 2026-10-16).
 */
export function membersCanOpen(
  screenRef: string,
  screens: readonly { key: string; ownerKind: string; ownerKey?: string | null }[],
  group: string,
): boolean {
  const s = screens.find((x) => x.key === screenRef);
  if (!s) return false;
  return s.ownerKind === "system" || (s.ownerKind === "org" && s.ownerKey === group);
}

/** The screener sections of `layout` whose screen members cannot open. */
export function sectionsMembersCannotOpen(
  layout: Layout,
  screens: readonly { key: string; ownerKind: string; ownerKey?: string | null }[],
  group: string,
): string[] {
  return layout.sections
    .filter((s) => s.kind === "screener")
    .filter((s) => {
      const ref = screenerConfig(s)?.screenRef;
      return !!ref && !membersCanOpen(ref, screens, group);
    })
    .map((s) => s.id);
}

/** What a panel takes from the screen members get instead. */
export interface MemberScreen {
  key: string;
  columns?: ScreenerConfig["columns"];
  filter?: ScreenerConfig["filter"];
  sort?: ScreenerConfig["sort"];
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  recs?: ScreenerConfig["recs"] | null;
  topDown?: ScreenerConfig["topDown"] | null;
  seasonality?: ScreenerConfig["seasonality"] | null;
}

/**
 * `layout` with each named section showing `instead` — its key and its whole
 * definition, so a member does not get the private screen's columns under
 * another screen's name. A Recs or TopDown block goes unless `instead` has one.
 */
export function withMemberScreen(layout: Layout, sectionIds: readonly string[], instead: MemberScreen): Layout {
  if (sectionIds.length === 0) return layout;
  return {
    ...layout,
    sections: layout.sections.map((s) => {
      if (!sectionIds.includes(s.id)) return s;
      const config = screenerConfig(s);
      if (!config) return s;
      const { recs: _recs, topDown: _topDown, seasonality: _seasonality, ...rest } = config;
      const next: ScreenerConfig = {
        ...rest,
        screenRef: instead.key,
        columns: instead.columns ?? [],
        filter: instead.filter,
        sort: instead.sort ?? [],
        ...(instead.savedFilters ? { savedFilters: instead.savedFilters } : {}),
        ...(instead.savedFilterCategoryOrder ? { savedFilterCategoryOrder: instead.savedFilterCategoryOrder } : {}),
        ...(instead.recs ? { recs: instead.recs } : {}),
        ...(instead.topDown ? { topDown: instead.topDown } : {}),
        ...(instead.seasonality ? { seasonality: instead.seasonality } : {}),
      };
      return { ...s, config: next };
    }),
  };
}
